import { call, type Effect, type ForkEffect, put, select, takeEvery } from "redux-saga/effects";
import { ApiActions } from "@touchnet/react-common";
import { actions } from "~/state/reducers/api-key-reducer";
import { cast } from "~/utils/fns.ts";
import Selectors from "~/state/selectors.ts";
import ApiKeyService from "~/services/api-key-service";
import { LOG } from "@touchnet/js-utils";
import {ApiKeyInfo, ApiKeyUpdateRequest} from "~/types/api-key-type.ts";
import {ForbiddenError, NotSupportedError} from "~/types/error-type.ts";
import {getApiKeyErrorMessage} from "~/errors/api-key-error.ts";


export function* fetchApiKey(action: ReturnType<typeof actions.fetchApiKey>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));
    try {
        const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
        const serverBase: string = cast<string>(yield select(Selectors.getServerBase));
        const apiKeyInfo: ApiKeyInfo = (yield call(
            ApiKeyService.fetchApiKey,
            cNum,
            serverBase)) as ApiKeyInfo;

        yield put(actions.receiveApiKey(apiKeyInfo));
    } catch (error) {
        if (error instanceof ForbiddenError) {
            yield put(actions.apiKeyForbidden());
            return;
        }
        if (error instanceof NotSupportedError) {
            yield put(actions.apiKeyNotSupported())
            return;
        }
        const errorMessage = getApiKeyErrorMessage(error);
        yield put(actions.receiveApiKeyFailed(errorMessage));
        LOG.error('FETCH_API_KEY: Error calling the api %o', [error]);
    } finally {
        yield put(ApiActions.completeApiCall(action.type));
    }
}

export function* updateApiKey(action: ReturnType<typeof actions.updateApiKey>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));
    try {
        const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
        const serverBase: string = cast<string>(yield select(Selectors.getServerBase));
        const payload: ApiKeyUpdateRequest = action.payload;
        const updatedApiKeyInfo: ApiKeyInfo = (yield call(
            ApiKeyService.updateApiKey,
            cNum,
            serverBase,
            payload)) as ApiKeyInfo;

        yield put(actions.receiveUpdatedApiKey(updatedApiKeyInfo));
    } catch (error) {
        if (error instanceof ForbiddenError) {
            yield put(actions.receiveUpdatedApiKeyFailed("You do not have permission to update the ERP API Key. Please contact your administrator."));
            return;
        }
        if (error instanceof NotSupportedError) {
            yield put(actions.receiveUpdatedApiKeyFailed("ERP API Key update is not supported yet. Please contact your administrator."))
            return;
        }
        const errorMessage = getApiKeyErrorMessage(error);
        yield put(actions.receiveUpdatedApiKeyFailed(errorMessage));
        LOG.error('UPDATE_API_KEY: Error calling the api %o', [error]);
    } finally {
        yield put(ApiActions.completeApiCall(action.type));
    }
}

export default function* rootSaga(): IterableIterator<ForkEffect> {
    yield takeEvery(actions.fetchApiKey.type, fetchApiKey);
    yield takeEvery(actions.updateApiKey.type, updateApiKey);
}
